import { ReactNode, FC, useEffect, useMemo, useState, useContext } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DevHubBox,
  DevHubButton,
  DevHubDynamicIcon,
  DevHubLink,
  DevHubTabbedPage,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { slugifyHyphen } from '../../util/stringFormat';
import { ApiPortalOrg } from './ApiPortalOrg';
import ApiPortalDocs from './ApiPortalDocs';
import { ApiPortalContext } from '../../contexts/ApiPortalContext';
import {
  ApplicationFeedbackButton,
  DevhubFeedbackProps,
} from '../insights/feedback/ApplicationFeedbackButton';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

interface ApiPortalTabbedPageProps {
  formattedOrgName: string;
  apiManagementContent?: ReactNode;
  dataFeedContent?: ReactNode;
  handleBackClick: () => void;
  additionalActions?: ReactNode;
}

/**
 * A component to render the API Portal tabbed page.
 * @param props the props of the component
 */
const ApiPortalTabbedPage: FC<ApiPortalTabbedPageProps> = (
  props: ApiPortalTabbedPageProps,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgName } = useParams<{ orgName?: string }>();
  const { org } = useContext(ApiPortalContext);

  const connectedLivingFeedbackProps: DevhubFeedbackProps = {
    applicationId: 124866,
    name: 'Connected Living API Portal',
  };

  const feedbackButton =
    org &&
    org._normalizedName === 'connected-living' &&
    ConfigHelper.instance.isFeatureEnabled(Feature.FEEDBACK) ? (
      <ApplicationFeedbackButton application={connectedLivingFeedbackProps} />
    ) : null;

  // Combine additionalActions with feedbackButton
  const combinedActions = (
    <>
      {props.additionalActions && (
        <DevHubBox display="inline-block" style={{ marginRight: '8px' }}>
          {props.additionalActions}
        </DevHubBox>
      )}
      {feedbackButton && (
        <DevHubBox display="inline-block">{feedbackButton}</DevHubBox>
      )}
    </>
  );

  const tabs = useMemo(
    () => [
      {
        label: 'API Management',
        route: `/api-portals/${orgName}`,
        content: (
          <>
            <DevHubBox
              tbPadding="large"
              display="flex"
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <DevHubTypography variant="h5" fontColor="blue">
                {props.formattedOrgName} Portal
              </DevHubTypography>
              <DevHubTypography variant="h4">
                Everything You Need To Build Incredible Experiences
              </DevHubTypography>
              <DevHubTypography>
                Explore how APIs are powering {props.formattedOrgName} by
                navigating through the categories below. Browse our full API
                catalog{' '}
                <DevHubLink
                  href={`/api-portals/${slugifyHyphen(
                    props.formattedOrgName,
                  )}/catalog`}
                >
                  here
                </DevHubLink>
                .
              </DevHubTypography>
              {orgName === 'connected-living' && (
                <DevHubTypography>
                  Quickly find answers to all your documentation questions with
                  our new{' '}
                  <DevHubLink
                    href="https://chatgpt.com/g/g-68d2ea3ca2f4819187d7a42809df24d4-connected-living-api-documentation-assistant?model=gpt-5"
                    target="_blank"
                  >
                    ChatGPT assistant
                  </DevHubLink>{' '}
                  (requires license).
                </DevHubTypography>
              )}
            </DevHubBox>
            <DevHubBox
              tbPadding="medium"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <DevHubButton variant="contained" onClick={props.handleBackClick}>
                <DevHubDynamicIcon iconName="ArrowBackIos" />
                Back
              </DevHubButton>
              {(props.additionalActions || feedbackButton) && (
                <DevHubBox>{combinedActions}</DevHubBox>
              )}
            </DevHubBox>
            {props.apiManagementContent ? (
              props.apiManagementContent
            ) : (
              <ApiPortalOrg isMainPage={false} />
            )}
          </>
        ),
      },
      {
        label: 'Data Feed Management',
        route: `/api-portals/${orgName}/data-feed-management`,
        content: (
          <>
            <DevHubBox
              tbPadding="large"
              display="flex"
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <DevHubTypography variant="h5" fontColor="blue">
                {props.formattedOrgName} Portal
              </DevHubTypography>
              <DevHubTypography variant="h4">
                Everything You Need To Build Incredible Experiences
              </DevHubTypography>
              <DevHubTypography>
                Explore how APIs are powering {props.formattedOrgName} by
                navigating through the categories below. Browse our full API
                catalog{' '}
                <DevHubLink
                  href={`/api-portals/${slugifyHyphen(
                    props.formattedOrgName,
                  )}/catalog`}
                >
                  here
                </DevHubLink>
                .
              </DevHubTypography>
              {orgName === 'connected-living' && (
                <DevHubTypography>
                  Quickly find answers to all your documentation questions with
                  our new{' '}
                  <DevHubLink
                    href="https://chatgpt.com/g/g-68d2ea3ca2f4819187d7a42809df24d4-connected-living-api-documentation-assistant?model=gpt-5"
                    target="_blank"
                  >
                    ChatGPT assistant
                  </DevHubLink>{' '}
                  (requires license).
                </DevHubTypography>
              )}
            </DevHubBox>
            <DevHubBox
              tbPadding="medium"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <DevHubButton variant="contained" onClick={props.handleBackClick}>
                <DevHubDynamicIcon iconName="ArrowBackIos" />
                Back
              </DevHubButton>
              {(props.additionalActions || feedbackButton) && (
                <DevHubBox>{combinedActions}</DevHubBox>
              )}
            </DevHubBox>
            {props.dataFeedContent ? (
              props.dataFeedContent
            ) : (
              <ApiPortalDocs isDataFeed />
            )}
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      orgName,
      org,
      props.formattedOrgName,
      props.apiManagementContent,
      props.dataFeedContent,
      props.handleBackClick,
      props.additionalActions,
      feedbackButton,
      combinedActions,
    ],
  );

  const getTabIndex = (path: string | undefined) => {
    if (path?.includes('data-feed-management')) {
      return 1;
    }
    return 0;
  };
  const initialTab = getTabIndex(location.pathname);
  const [currentTab, setCurrentTab] = useState(initialTab);

  useEffect(() => {
    const newIndex = getTabIndex(location.pathname);
    if (newIndex !== currentTab) {
      setCurrentTab(newIndex);
    } else if (!location.pathname) {
      navigate(tabs[0].route, { replace: true });
    }
  }, [location.pathname, navigate, currentTab, tabs]);

  return (
    <DevHubTabbedPage
      title={`${props.formattedOrgName} Portal`}
      subtitle="Explore the APIs and Data Feeds that power our ecosystem"
      tabs={tabs}
      navigate={navigate}
      currentTab={currentTab}
    >
      {tabs[0].content}
    </DevHubTabbedPage>
  );
};

export default ApiPortalTabbedPage;
